import { NS_PER_S_BIGINT } from "./time";

/**
 * Duration utility for converting between seconds/nanoseconds and duration components.
 * Avoids precision loss and provides compatibility with Intl.DurationFormat.
 * Mimics Temporal.Duration API for future migration when it becomes baseline.
 */
export class Duration {
  private readonly hours: number;
  private readonly minutes: number;
  private readonly seconds: number;
  private readonly nanoseconds: number;

  private constructor(
    hours: number,
    minutes: number,
    seconds: number,
    nanoseconds: number,
  ) {
    this.hours = hours;
    this.minutes = minutes;
    this.seconds = seconds;
    this.nanoseconds = nanoseconds;
  }

  /**
   * Creates a Duration from either seconds or nanoseconds.
   * Uses string-based conversion to avoid precision loss for large values.
   */
  static from(input: { seconds: number } | { nanoseconds: bigint }): Duration {
    let totalNanos: bigint;

    if ("seconds" in input) {
      const secondsStr = input.seconds.toString();
      const [wholePart, fractionalPart = ""] = secondsStr.split(".");

      totalNanos = BigInt(wholePart) * NS_PER_S_BIGINT;

      if (fractionalPart) {
        // Pad or truncate to 9 digits (nanosecond precision)
        const nanoStr = fractionalPart.padEnd(9, "0").slice(0, 9);
        totalNanos += BigInt(nanoStr);
      }
    } else {
      totalNanos = input.nanoseconds;
    }

    const NS_PER_SECOND = 1_000_000_000n;
    const NS_PER_MINUTE = 60_000_000_000n;
    const NS_PER_HOUR = 3_600_000_000_000n;

    const hours = totalNanos / NS_PER_HOUR;
    const remainder1 = totalNanos % NS_PER_HOUR;

    const minutes = remainder1 / NS_PER_MINUTE;
    const remainder2 = remainder1 % NS_PER_MINUTE;

    const seconds = remainder2 / NS_PER_SECOND;
    const nanoseconds = remainder2 % NS_PER_SECOND;

    return new Duration(
      Number(hours),
      Number(minutes),
      Number(seconds),
      Number(nanoseconds),
    );
  }

  /** Returns duration components for use with Intl.DurationFormat. */
  toObject(): {
    hours: number;
    minutes: number;
    seconds: number;
    nanoseconds: number;
  } {
    return {
      hours: this.hours,
      minutes: this.minutes,
      seconds: this.seconds,
      nanoseconds: this.nanoseconds,
    };
  }
}
